import React, { useState, useEffect } from 'react';
import './AdminPortal.css';
import { isAuthenticated, setCredentials, clearCredentials, getAuthHeader } from '../../api/authApi';
import { techApi, Tech } from '../../api/techApi';
import { servicesApi, Service } from '../../api/servicesApi';
import { projectsApi, Project } from '../../api/projectsApi';

type Tab = 'tech' | 'services' | 'projects';

const AdminPortal: React.FC = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<Tab>('tech');
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);

    // Common Form States
    const [displayOrder, setDisplayOrder] = useState(0);
    const [isActive, setIsActive] = useState(true);

    // Tech Form States
    const [techName, setTechName] = useState('');

    // Service Form States
    const [serviceTitle, setServiceTitle] = useState('');
    const [serviceDesc, setServiceDesc] = useState('');
    const [serviceIconType, setServiceIconType] = useState<'EMOJI' | 'FONT_AWESOME' | 'MATERIAL_ICON' | 'SVG' | 'IMAGE_URL'>('EMOJI');
    const [serviceIconValue, setServiceIconValue] = useState('');

    // Project Form States
    const [projectTitle, setProjectTitle] = useState('');
    const [projectDesc, setProjectDesc] = useState('');
    const [projectStatus, setProjectStatus] = useState<'LIVE' | 'IN_DEVELOPMENT' | 'PLANNING' | 'COMPLETED' | 'ON_HOLD'>('PLANNING');
    const [projectUrl, setProjectUrl] = useState('');
    const [projectTechs, setProjectTechs] = useState('');
    const [isFeatured, setIsFeatured] = useState(false);

    useEffect(() => {
        if (isLoggedIn) {
            loadData();
        }
    }, [isLoggedIn, activeTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            let result;
            if (activeTab === 'tech') result = await techApi.getAllTech();
            else if (activeTab === 'services') result = await servicesApi.getAllServicesAdmin();
            else if (activeTab === 'projects') result = await projectsApi.getAllProjectsAdmin();
            setData(result || []);
        } catch (err) {
            console.error('Failed to load admin data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        if (username && password) {
            setCredentials(username, password);
            fetch('/api/v1/test', { headers: getAuthHeader() })
                .then(res => {
                    if (res.ok) {
                        setIsLoggedIn(true);
                        setError('');
                    } else {
                        setError('Invalid credentials');
                        clearCredentials();
                    }
                })
                .catch(() => setError('Server error during login'));
        }
    };

    const handleLogout = () => {
        clearCredentials();
        setIsLoggedIn(false);
    };

    const openModal = (item: any = null) => {
        setEditingItem(item);
        if (activeTab === 'tech') {
            setTechName(item ? item.name : '');
            setDisplayOrder(item ? item.displayOrder : data.length + 1);
        } else if (activeTab === 'services') {
            setServiceTitle(item ? item.title : '');
            setServiceDesc(item ? item.description : '');
            setServiceIconType(item ? item.iconType : 'EMOJI');
            setServiceIconValue(item ? item.iconValue : '');
            setDisplayOrder(item ? item.displayOrder : data.length + 1);
            setIsActive(item ? item.isActive : true);
        } else if (activeTab === 'projects') {
            setProjectTitle(item ? item.title : '');
            setProjectDesc(item ? item.description : '');
            setProjectStatus(item ? item.projectStatus : 'PLANNING');
            setProjectUrl(item ? item.projectUrl : '#');
            setProjectTechs(item ? item.technologies.join(', ') : '');
            setDisplayOrder(item ? item.displayOrder : data.length + 1);
            setIsActive(item ? item.isActive : true);
            setIsFeatured(item ? item.isFeatured : false);
        }
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {
            if (activeTab === 'tech') await techApi.deleteTech(id);
            else if (activeTab === 'services') await servicesApi.deleteService(id);
            else if (activeTab === 'projects') await projectsApi.deleteProject(id);
            loadData();
        } catch (err) {
            alert('Delete failed. Please check permissions.');
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (activeTab === 'tech') {
                const techData = { name: techName, displayOrder };
                if (editingItem) await techApi.updateTech(editingItem.id, techData);
                else await techApi.addTech(techData);
            } else if (activeTab === 'services') {
                const serviceData: Omit<Service, 'id'> = {
                    title: serviceTitle,
                    description: serviceDesc,
                    iconType: serviceIconType,
                    iconValue: serviceIconValue,
                    displayOrder,
                    isActive
                };
                if (editingItem) await servicesApi.updateService(editingItem.id, serviceData);
                else await servicesApi.addService(serviceData);
            } else if (activeTab === 'projects') {
                const projectData: Omit<Project, 'id'> = {
                    title: projectTitle,
                    description: projectDesc,
                    projectStatus,
                    projectUrl,
                    technologies: projectTechs.split(',').map((s: string) => s.trim()).filter((s: string) => s),
                    displayOrder,
                    isActive,
                    isFeatured
                };
                if (editingItem) await projectsApi.updateProject(editingItem.id, projectData);
                else await projectsApi.addProject(projectData);
            }
            setIsModalOpen(false);
            loadData();
        } catch (err) {
            alert('Save failed. Please check backend logs.');
        }
    };

    if (!isLoggedIn) {
        return (
            <div className="admin-portal">
                <div className="admin-login">
                    <h2>Workshop Gate</h2>
                    <form onSubmit={handleLogin}>
                        <div className="form-group">
                            <label>Username</label>
                            <input type="text" value={username} onChange={e => setUsername(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Password</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                        </div>
                        <button type="submit" className="login-button">Unlock Gate</button>
                    </form>
                    {error && <p className="error-message">{error}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-portal">
            <div className="admin-dashboard">
                <header className="admin-header">
                    <h1>Backstage Management</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <a href="/" style={{ color: '#3b82f6', fontSize: '14px', textDecoration: 'none' }}>← View Live Site</a>
                        <span className="logout-link" onClick={handleLogout}>Lock Gate & Logout</span>
                    </div>
                </header>

                <nav className="admin-nav">
                    <div className={`nav-item ${activeTab === 'tech' ? 'active' : ''}`} onClick={() => setActiveTab('tech')}>Tech Stack</div>
                    <div className={`nav-item ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>Services</div>
                    <div className={`nav-item ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => setActiveTab('projects')}>Projects</div>
                </nav>

                <div className="crud-container">
                    <div className="crud-header">
                        <h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} List</h2>
                        <button className="add-button" onClick={() => openModal()}>
                            + Add {activeTab.slice(0, -1)}
                        </button>
                    </div>

                    {loading ? (
                        <p>Synchronizing data...</p>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{activeTab === 'tech' ? 'Name' : 'Title'}</th>
                                    <th>Order</th>
                                    {activeTab !== 'tech' && <th>Status</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item: any) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.title || item.name}</td>
                                        <td>{item.displayOrder}</td>
                                        {activeTab !== 'tech' && <td>{item.isActive ? '✅ Active' : '❌ Hidden'}</td>}
                                        <td className="action-buttons">
                                            <span className="edit-btn" onClick={() => openModal(item)}>Edit</span>
                                            <span className="delete-btn" onClick={() => handleDelete(item.id)}>Delete</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="admin-login" style={{ marginTop: '50px', maxWidth: '600px', width: '90%' }}>
                        <h3>{editingItem ? `Edit ${activeTab.slice(0, -1)}` : `New ${activeTab.slice(0, -1)}`}</h3>
                        <form onSubmit={handleSave}>
                            {activeTab === 'tech' && (
                                <div className="form-group">
                                    <label>Tech Name</label>
                                    <input type="text" value={techName} onChange={e => setTechName(e.target.value)} required />
                                </div>
                            )}

                            {activeTab === 'services' && (
                                <>
                                    <div className="form-group">
                                        <label>Service Title</label>
                                        <input type="text" value={serviceTitle} onChange={e => setServiceTitle(e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea value={serviceDesc} onChange={e => setServiceDesc(e.target.value)} rows={4} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px' }} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Icon Type</label>
                                        <select value={serviceIconType} onChange={e => setServiceIconType(e.target.value as any)} style={{ width: '100%', background: '#1a1a1b', color: '#fff', padding: '10px', borderRadius: '8px' }}>
                                            <option value="EMOJI">Emoji</option>
                                            <option value="FONT_AWESOME">Font Awesome</option>
                                            <option value="MATERIAL_ICON">Material Icon</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Icon Value (e.g. 🚀 or fa-code)</label>
                                        <input type="text" value={serviceIconValue} onChange={e => setServiceIconValue(e.target.value)} required />
                                    </div>
                                </>
                            )}

                            {activeTab === 'projects' && (
                                <>
                                    <div className="form-group">
                                        <label>Project Title</label>
                                        <input type="text" value={projectTitle} onChange={e => setProjectTitle(e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea value={projectDesc} onChange={e => setProjectDesc(e.target.value)} rows={3} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px' }} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select value={projectStatus} onChange={e => setProjectStatus(e.target.value as any)} style={{ width: '100%', background: '#1a1a1b', color: '#fff', padding: '10px', borderRadius: '8px' }}>
                                            <option value="LIVE">Live</option>
                                            <option value="IN_DEVELOPMENT">In Development</option>
                                            <option value="PLANNING">Planning</option>
                                            <option value="COMPLETED">Completed</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Technologies (comma separated)</label>
                                        <input type="text" value={projectTechs} onChange={e => setProjectTechs(e.target.value)} placeholder="React, TypeScript, Java" />
                                    </div>
                                    <div className="form-group">
                                        <label>URL</label>
                                        <input type="text" value={projectUrl} onChange={e => setProjectUrl(e.target.value)} />
                                    </div>
                                    <div className="form-group" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} />
                                        <label style={{ margin: 0 }}>Featured Project</label>
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label>Display Order</label>
                                <input type="number" value={displayOrder} onChange={e => setDisplayOrder(parseInt(e.target.value))} required />
                            </div>

                            {activeTab !== 'tech' && (
                                <div className="form-group" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                                    <label style={{ margin: 0 }}>Active / Visible</label>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button type="submit" className="login-button">Save Changes</button>
                                <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '12px', borderRadius: '8px', flex: 1, cursor: 'pointer' }}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPortal;
